<script setup>
const emit = defineEmits(['success', 'toggle'])

const { AnalyticsEvent, track } = useAnalytics()
const tech = useTech()
const layoutTheme = computed(() => tech.currentTheme || 'theme-lapiz ')
const techTheme = useTechTheme(tech.id)
const currentTheme = techTheme.lowestLevelTheme

const props = defineProps({
  queue: {
    type: Object,
    default: null
  }
})

const ui = useUi()
const user = useUser()
const { checkAndRedirectToProfileCompletion } = useProfileCompletion()

const route = useRoute()

const termsLink = '/legal/cert-terms-conditions'
const errors = ref({})
const loading = ref(false)
const inputPassword = ref(null)
const requireName = ref(false)
const name = ref('')

const agreedToShare = ref(false)
const form = reactive({
  first_name: null,
  last_name: null,
  email: route.query?.email || '',
  password: ''
})

watch(() => route.query, (query) => {
  if (query?.email) {
    form.email = query.email
  }
})

async function submit () {
  errors.value = null
  loading.value = true

  const [firstName, ...lastNameArray] = name.value.split(' ')
  const lastName = lastNameArray.join(' ')

  if (props.queue) user.addToQueue(props.queue)

  try {
    await user.register({
      ...toRaw(form),
      last_name: lastName,
      first_name: firstName,
      linkedin_achievement_share_permission: agreedToShare.value
    })
    track(AnalyticsEvent.LEAD_SIGNUP, {
      provider: 'email'
    })
    ui.closeAuthModal()

    // Check profile completion before emitting success
    const wasRedirected = await checkAndRedirectToProfileCompletion()

    if (wasRedirected) {
      return
    }

    emit('success')

    track(AnalyticsEvent.SIGNUP, {
      provider: 'email',
      email: form.email,
      name: `${firstName ?? ''} ${lastName ?? ''}`
    })
  } catch (e) {
    errors.value = e.data?.errors
    loading.value = false
  }
}

async function onBeforeAuthSignupRedirect (social) {
  track(AnalyticsEvent.LEAD_AUTH_SIGNUP, {
    provider: social.name
  })
}
</script>

<template>
  <div
    class="flex flex-col gap-6 rounded-3xl bg-white px-4 py-6 md:w-130 md:p-9 dark:bg-dark-gunmetal"
    :class="[{'dark:bg-dark-metal': tech.id === 'nuxt'}, layoutTheme]">
    <h1
      class="text-3xl font-bold md:text-5xl"
      :class="currentTheme?.heading || 'heading-primary'">
      <em>Sign Up</em>
    </h1>
    <span class="text-sm dark:text-white">Your choice could be the next certification program. Sign up for updates, share your input, and don’t miss out.</span>

    <AuthVoucherMessage />

    <AuthSocial
      v-bind="{ mode: 'register', queue }"
      @before-redirect="onBeforeAuthSignupRedirect" />

    <AuthDivider />

    <form
      class="flex w-full flex-col gap-6"
      @submit.prevent="submit">
      <FormInput
        v-model="name"
        required
        type="text"
        label="Your Full Name"
        placeholder="Enter your full name" />

      <FormInput
        v-if="requireName"
        v-model="form.last_name"
        required
        type="text"
        placeholder="Enter your name"
        label="Your Last name"
        :errors="errors?.last_name" />

      <FormInput
        v-model="form.email"
        required
        type="email"
        placeholder="Enter your email"
        label="Your Email"
        :errors="errors?.email" />

      <FormInput
        ref="inputPassword"
        v-model="form.password"
        required
        type="password"
        placeholder="Enter your password"
        label="Choose Your Password"
        :errors="errors?.password" />

      <ShareAchievementCheckbox
        v-model:checked="agreedToShare"
        mode="model"
        class="select-none text-quartz dark:text-body-gray">
        I give my permission for Certificates.dev to share my achievement on LinkedIn for me once I am certified
      </ShareAchievementCheckbox>

      <div class="text-center text-sm font-light text-quartz dark:text-body-gray">
        <span>
          By creating this account you agreed with our
        </span>
        <NuxtLink
          :to="termsLink"
          target="_blank"
          class="underline">
          Terms & Conditions
        </NuxtLink>
      </div>

      <button
        :disabled="loading"
        class="w-full px-8 py-4"
        :class="currentTheme?.button || 'btn-primary'">
        <AppLoading v-if="loading" /> Sign Up
      </button>
    </form>

    <button
      :class="currentTheme?.buttonLink || 'btn-primary-link'"
      @click="emit('toggle')">
      I already have an account
    </button>
  </div>
</template>
